/**
 * Shared request runner for source-backed components.
 *
 * WHY THIS EXISTS: every source-backed widget needs the same four things —
 * build a `RyRequest`, abort whatever it had in flight, guard against a stale
 * response landing after a fresh one, and reflect `data-ry-state` +
 * `ry:load` / `ry:loaded` / `ry:load-error`. Implementing that per widget would
 * duplicate the one piece of logic the data contract calls load-bearing.
 *
 * WHY IT'S PER-CONSUMER, NOT PER-SOURCE: the contract explicitly encourages two
 * widgets to share one source (`<ry-table source="orders">` +
 * `<ry-chart source="orders">`). If the lifecycle lived on the source element,
 * each widget's load would abort the other's and they'd fight forever. So the
 * source is a *provider of transport* and each consumer owns its own runner,
 * its own controller, and its own state. Two widgets means two requests — see
 * open question #4 in docs/arch/data-contract.md; deduping them is a future
 * optimization the fixed vocabulary keeps available.
 *
 * This module holds NO transport. It calls the `RySource` it's handed.
 */

import type { RyElement } from './ry-element.js';
import {
  isAbort,
  isRetryable,
  RyTimeoutError,
  type RyDataState,
  type RyErrorDetail,
  type RyLoadDetail,
  type RyLoadedDetail,
  type RyRequest,
  type RyResponse,
  type RySource,
} from './ry-data.js';

/** The parts of a request a caller supplies; `signal` is always the runner's. */
export type RyRequestInit = Omit<RyRequest, 'signal'>;

export interface RyLoaderOptions {
  /** Extra `meta` merged under the caller's, e.g. from a `[meta]` attribute. */
  baseMeta?: () => Record<string, unknown>;
  /** Abort after this many ms. 0 / undefined = no limit. */
  timeoutMs?: () => number;
  /** Extra attempts after a RETRYABLE failure. 0 = none. */
  retries?: () => number;
  /** Base backoff in ms; doubles per attempt. Default 300. */
  retryDelayMs?: () => number;
  /** Injected for tests so backoff doesn't burn real time. */
  sleep?: (ms: number) => Promise<void>;
}

export class RyLoaderRunner {
  #host: RyElement;
  #options: RyLoaderOptions;
  #controller: AbortController | null = null;
  #timer: ReturnType<typeof setInterval> | null = null;
  #visibilityBound = false;
  #debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(host: RyElement, options: RyLoaderOptions = {}) {
    this.#host = host;
    this.#options = options;
  }

  /** Cancel the in-flight request. Not an error — emits nothing. */
  abort(): void {
    this.#controller?.abort();
    this.#controller = null;
    this.#host.removeAttribute('aria-busy');
  }

  get pending(): boolean {
    return this.#controller !== null;
  }

  /**
   * Set `data-ry-state` without going through RyElement's change-guarded
   * setter. Used for initialization: the guard compares against the getter,
   * and a component that narrows `state` with its own default would see no
   * transition and never write the attribute. Mounting isn't a transition, so
   * it shouldn't emit `ry:state-change` either.
   */
  static initState(host: RyElement, state: RyDataState = 'idle'): void {
    if (!host.hasAttribute('data-ry-state')) host.dataset.ryState = state;
  }

  /**
   * Poll on an interval. Two rules make this safe rather than a footgun:
   *
   *  - A hidden tab does NOT poll. Twenty background tabs on a 5s interval is a
   *    self-inflicted DDoS, and the user can't see the result anyway. Becoming
   *    visible again triggers one immediate catch-up so the view isn't stale.
   *  - A tick with a request already in flight is SKIPPED, not queued. Polls
   *    stacking on a slow endpoint is how you turn a slow server into a dead one.
   *
   * Idempotent: calling it again replaces the interval.
   */
  startPolling(intervalMs: number, run: () => void): void {
    this.stopPolling();
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) return;

    this.#timer = setInterval(() => {
      if (this.#hidden() || this.pending) return;
      run();
    }, intervalMs);

    if (!this.#visibilityBound) {
      this.#visibilityBound = true;
      // host.on() so it's torn down with the element — the runner has no
      // lifecycle of its own.
      this.#host.on(this.#host.ownerDocument, 'visibilitychange', () => {
        if (!this.#timer || this.#hidden() || this.pending) return;
        run();
      });
    }
  }

  /**
   * Delay `run` by `ms`, cancelling any pending call. Composes WITH abort
   * rather than replacing it: debounce suppresses requests that were never
   * worth sending; abort supersedes ones already in flight. A search box needs
   * both — the first stops you firing on every keystroke, the second stops an
   * early response landing after a later one.
   */
  debounce(ms: number, run: () => void): void {
    if (this.#debounceTimer !== null) clearTimeout(this.#debounceTimer);
    if (!Number.isFinite(ms) || ms <= 0) { run(); return; }
    this.#debounceTimer = setTimeout(() => { this.#debounceTimer = null; run(); }, ms);
  }

  cancelDebounce(): void {
    if (this.#debounceTimer !== null) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = null;
  }

  stopPolling(): void {
    if (this.#timer !== null) clearInterval(this.#timer);
    this.#timer = null;
  }

  get polling(): boolean {
    return this.#timer !== null;
  }

  #hidden(): boolean {
    return this.#host.ownerDocument.visibilityState === 'hidden';
  }

  async run(
    fetcher: RySource,
    init: Partial<RyRequestInit> = {}
  ): Promise<RyResponse | null> {
    // Supersede whatever was in flight. This is the whole reason `signal` is
    // required by the contract: without it an earlier response can land after
    // a later one and render stale rows that look correct.
    this.abort();
    const controller = new AbortController();
    this.#controller = controller;

    const request: RyRequest = {
      ...init,
      meta: { ...(this.#options.baseMeta?.() ?? {}), ...(init.meta ?? {}) },
      signal: controller.signal,
    };

    if (!this.#host.emit<RyLoadDetail>('load', { request })) {
      this.#controller = null;
      return null;
    }

    // Arm the deadline. A hung request otherwise hangs forever — and because a
    // poll tick is skipped while one is in flight, it silently wedges polling
    // too. Aborting WITH a reason is what lets the catch below tell "we ran out
    // of time" (an error the user must see) from "we were superseded" (normal).
    const timeoutMs = this.#options.timeoutMs?.() ?? 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (timeoutMs > 0) {
      timer = setTimeout(() => controller.abort(new RyTimeoutError(timeoutMs)), timeoutMs);
    }
    const clearDeadline = (): void => { if (timer !== null) clearTimeout(timer); timer = null; };

    // E8: a load with content already on screen is a REFRESH, not a cold start.
    // `loading` means "nothing to show, render a skeleton"; `refreshing` means
    // "keep what's there and dim it". Collapsing them makes blanking the table
    // on every sort click the likely default.
    const hasContent = this.#host.state === 'ready' || this.#host.state === 'refreshing';
    this.#setState(hasContent ? 'refreshing' : 'loading');
    this.#host.setAttribute('aria-busy', 'true');

    try {
      const response = await this.#attempt(fetcher, request, controller);
      // Shape guard. A source can return a read, a stream, or a subscription
      // (data-contract-patterns.md E1). A widget that asked for rows and got an
      // async iterable would otherwise read `undefined.length` three lines down
      // — fail with something that names the actual mistake.
      if (!response || !Array.isArray(response.rows)) {
        throw new TypeError(
          'ry-ui: this source did not return rows. A widget reading the WINDOW shape ' +
            'got a stream or subscription instead — use source.stream() / source.subscribe() ' +
            'for those. See docs/arch/data-contract-patterns.md.'
        );
      }
      // A newer run() replaced us while awaiting — drop this result rather
      // than clobbering fresher rows.
      if (controller.signal.aborted || this.#controller !== controller) return null;
      this.#controller = null;

      clearDeadline();
      this.#host.removeAttribute('aria-busy');
      this.#setState(response.rows.length === 0 ? 'empty' : 'ready');
      this.#host.emit<RyLoadedDetail>('loaded', { ...response, request });
      return response;
    } catch (error) {
      clearDeadline();
      // A timeout arrives as an abort, but it is NOT one — surface it.
      const reason: unknown = controller.signal.reason;
      const timedOut = reason instanceof RyTimeoutError || error instanceof RyTimeoutError;
      if (!timedOut && (controller.signal.aborted || isAbort(error))) return null;
      if (this.#controller === controller) this.#controller = null;
      this.#host.removeAttribute('aria-busy');
      this.#setState('error');
      this.#host.emit<RyErrorDetail>('load-error', {
        error: timedOut && reason instanceof RyTimeoutError ? reason : error,
        request,
      });
      return null;
    } finally {
      clearDeadline();
    }
  }

  /**
   * Call the fetcher, retrying RETRYABLE failures with exponential backoff.
   *
   * Only retryable ones: a 404 will be a 404 next time, and retrying it wastes
   * a round trip while delaying the error the user needs. A 5xx, a 429, a
   * timeout, or a dead network are all plausibly transient.
   */
  async #attempt(
    fetcher: RySource,
    request: RyRequest,
    controller: AbortController
  ): Promise<RyResponse> {
    const retries = this.#options.retries?.() ?? 0;
    const base = this.#options.retryDelayMs?.() ?? 300;
    const sleep = this.#options.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));

    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await fetcher(request);
      } catch (error) {
        lastError = error;
        const timedOut = controller.signal.reason instanceof RyTimeoutError;
        // Never retry a supersede — a newer request already replaced this one.
        if (controller.signal.aborted && !timedOut) throw error;
        if (attempt === retries || !isRetryable(error)) throw error;
        await sleep(base * 2 ** attempt);
        if (controller.signal.aborted && !(controller.signal.reason instanceof RyTimeoutError)) throw error;
      }
    }
    throw lastError;
  }

  #setState(state: RyDataState): void {
    this.#host.state = state;
  }
}
